/* ==========================================================================
   KINORA — carousels + FAQ accordion

   Everything here is progressive enhancement:
   - carousels already scroll and snap with CSS alone; JS adds dots, arrows,
     the centred start position and autoplay
   - the FAQ is a native <details>; JS animates the height and keeps one open

   Carousel markup contract:
     data-carousel                 on the root
     data-carousel-track           on the scroller
     data-carousel-prev/next       arrows (inside the root, or anywhere with
                                   the root's id as the value)
     data-carousel-dots="<id>"     dots container to populate
     data-carousel-start="center"  open on the middle item
     data-carousel-autoplay="<ms>" advance on a timer
     data-carousel-loop            wrap around seamlessly
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     Carousel
     ------------------------------------------------------------------------ */
  function initCarousel(root) {
    var track = root.querySelector('[data-carousel-track]');
    if (!track) return;

    // Controls may sit inside the carousel (hero) or elsewhere on the page and
    // point back at it by id (questions).
    var prev = root.querySelector('[data-carousel-prev]') ||
               (root.id && document.querySelector('[data-carousel-prev="' + root.id + '"]'));
    var next = root.querySelector('[data-carousel-next]') ||
               (root.id && document.querySelector('[data-carousel-next="' + root.id + '"]'));

    var dotsId = root.getAttribute('data-carousel-dots');
    var dotsBox = dotsId ? document.getElementById(dotsId) : null;

    var loop = root.hasAttribute('data-carousel-loop');
    var autoplayMs = parseInt(root.getAttribute('data-carousel-autoplay'), 10) || 0;
    var startCentre = root.getAttribute('data-carousel-start') === 'center';

    function items() {
      return Array.prototype.slice.call(track.children);
    }

    // How many items the visitor is meant to perceive — clones don't count.
    var realCount = track.children.length;

    // Index of the first real slide once clones are added (0 when not looping).
    var loopOffset = 0;

    // For a seamless loop we need a full copy of the set on BOTH sides, so that
    // whichever slide is centred always has real neighbours to its left and
    // right. Teleporting between the identical copies is then invisible — with
    // clones on one side only, the wrap left an empty gap beside the edge slide.
    if (loop) {
      var reals = items();
      var mark = function (node) {
        node.setAttribute('aria-hidden', 'true');
        node.setAttribute('data-carousel-clone', '');
        // Clones sit off-screen, so their lazy images would flash in blank when
        // the loop teleports onto them — load them up front (same cached URLs).
        Array.prototype.forEach.call(node.querySelectorAll('img'), function (im) {
          if (im.dataset && im.dataset.src) im.src = im.dataset.src;
          im.loading = 'eager';
        });
        return node;
      };
      // Trailing copy.
      reals.forEach(function (el) { track.appendChild(mark(el.cloneNode(true))); });
      // Leading copy (insert in reverse to keep original order).
      for (var c = reals.length - 1; c >= 0; c--) {
        track.insertBefore(mark(reals[c].cloneNode(true)), track.firstChild);
      }
      loopOffset = realCount;
    }

    // Item index → logical slide (0..realCount-1), unwrapping the clones.
    function logicalIndex() {
      return ((activeIndex() - loopOffset) % realCount + realCount) % realCount;
    }

    // Scroll offset that puts item `i` where its snap alignment says it belongs.
    function targetFor(i) {
      var item = items()[i];
      if (!item) return track.scrollLeft;

      var delta = item.getBoundingClientRect().left - track.getBoundingClientRect().left;
      var align = getComputedStyle(item).scrollSnapAlign || '';

      if (align.indexOf('center') !== -1) {
        return track.scrollLeft + delta - (track.clientWidth - item.offsetWidth) / 2;
      }
      var padStart = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
      return track.scrollLeft + delta - padStart;
    }

    // The item currently closest to where it would rest if it were active.
    // Derived from targetFor, so it stays right for both start- and
    // centre-aligned tracks instead of assuming one of them.
    function activeIndex() {
      var best = 0;
      var bestDist = Infinity;

      items().forEach(function (_, i) {
        var dist = Math.abs(targetFor(i) - track.scrollLeft);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    function goTo(i, instant) {
      var last = items().length - 1;
      var clamped = Math.max(0, Math.min(i, last));
      track.scrollTo({
        left: targetFor(clamped),
        // 'instant' rather than 'auto': 'auto' defers to the CSS
        // scroll-behavior, which is smooth here.
        behavior: (instant || reduceMotion) ? 'instant' : 'smooth'
      });
    }

    /* --- Dots --- */
    var dots = [];
    if (dotsBox) {
      dotsBox.innerHTML = '';
      for (var i = 0; i < realCount; i++) {
        (function (n) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel__dot';
          dot.setAttribute('aria-label', 'Przejdź do slajdu ' + (n + 1));
          dot.addEventListener('click', function () { goTo(loopOffset + n); });
          dotsBox.appendChild(dot);
          dots.push(dot);
        })(i);
      }
    }

    /* --- Arrows --- */
    if (prev) prev.addEventListener('click', function () { goTo(activeIndex() - 1); });
    if (next) next.addEventListener('click', function () { goTo(activeIndex() + 1); });

    /* --- State sync --- */
    function sync() {
      var i = logicalIndex();

      dots.forEach(function (dot, n) {
        dot.classList.toggle('is-active', n === i);
        dot.setAttribute('aria-current', n === i ? 'true' : 'false');
      });

      // Looping carousels never run out of room in either direction.
      if (!loop) {
        // 1px of slack absorbs sub-pixel scroll positions.
        var atStart = track.scrollLeft <= 1;
        var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
        if (prev) prev.disabled = atStart;
        if (next) next.disabled = atEnd;
      }
    }

    // Once the scroll rests on a clone, teleport to the identical real slide in
    // the middle set. Works both ways: past the trailing copy jumps back a set,
    // into the leading copy jumps forward a set.
    function onSettle() {
      if (!loop) return;
      var i = activeIndex();
      if (i >= loopOffset + realCount) goTo(i - realCount, true);
      else if (i < loopOffset) goTo(i + realCount, true);
    }

    var queued = false;
    var settleTimer = null;
    track.addEventListener('scroll', function () {
      if (!queued) {
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          sync();
        });
      }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(onSettle, 120);
    }, { passive: true });

    window.addEventListener('resize', sync);

    /* --- Autoplay --- */
    if (autoplayMs && !reduceMotion) {
      var timer = null;

      var play = function () {
        if (!timer) timer = setInterval(function () { goTo(activeIndex() + 1); }, autoplayMs);
      };
      var pause = function () {
        clearInterval(timer);
        timer = null;
      };

      // Never fight the visitor for control of the scroller.
      root.addEventListener('pointerenter', pause);
      root.addEventListener('pointerleave', play);
      root.addEventListener('pointerdown', pause);
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', play);

      // And don't churn while the section is off screen.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries[0].isIntersecting ? play() : pause();
        }, { threshold: 0.25 }).observe(root);
      } else {
        play();
      }
    }

    /* --- Initial position --- */
    // Land on the first real slide (middle set when looping); centre-start
    // nudges to the middle real slide instead.
    var startIndex = loopOffset + (startCentre && realCount > 1 ? Math.floor(realCount / 2) : 0);
    if (startIndex > 0) {
      goTo(startIndex, true);
    }
    sync();
  }

  /* ------------------------------------------------------------------------
     Hero fade slider

     A cross-fade (the Swiper "effect-fade" look) rather than a scrolling
     track, so this is its own component: with fade every slide is stacked in
     the same spot, which rules out both scroll-snap swiping and the browser's
     lazy loading. Both are handled here instead.
     ------------------------------------------------------------------------ */
  function initHeroSlider(root) {
    var wrap = root.querySelector('[data-hero-slides]');
    if (!wrap) return;

    var slides = Array.prototype.slice.call(wrap.children);
    if (slides.length < 2) return;

    var dotsId = root.getAttribute('data-hero-dots');
    var dotsBox = dotsId ? document.getElementById(dotsId) : null;
    var prev = root.querySelector('[data-hero-prev]');
    var next = root.querySelector('[data-hero-next]');
    var autoplayMs = parseInt(root.getAttribute('data-hero-autoplay'), 10) || 0;

    var index = slides.findIndex(function (s) { return s.classList.contains('is-active'); });
    if (index < 0) index = 0;

    var dots = [];
    var timer = null;
    var stopped = false;

    // Slides past the first ship a data-src, so nothing but the LCP image is
    // requested up front.
    function loadSlide(i) {
      var img = slides[i] && slides[i].querySelector('img[data-src]');
      if (!img) return;
      img.src = img.getAttribute('data-src');
      img.removeAttribute('data-src');
    }

    function show(i) {
      index = (i + slides.length) % slides.length;

      slides.forEach(function (slide, n) {
        slide.classList.toggle('is-active', n === index);
      });
      dots.forEach(function (dot, n) {
        dot.classList.toggle('is-active', n === index);
        dot.setAttribute('aria-current', n === index ? 'true' : 'false');
      });

      // Keep one slide ahead warm so the next fade is never blank.
      loadSlide((index + 1) % slides.length);
    }

    // "Autoplay stops after manual interaction" — for good, not just paused.
    function stopAutoplay() {
      stopped = true;
      clearInterval(timer);
      timer = null;
    }

    function manual(i) {
      stopAutoplay();
      show(i);
    }

    /* --- Dots --- */
    if (dotsBox) {
      dotsBox.innerHTML = '';
      slides.forEach(function (_, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'carousel__dot' + (i === index ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Zdjęcie ' + (i + 1) + ' z ' + slides.length);
        dot.addEventListener('click', function () { manual(i); });
        dotsBox.appendChild(dot);
        dots.push(dot);
      });
    }

    /* --- Arrows --- */
    if (prev) prev.addEventListener('click', function () { manual(index - 1); });
    if (next) next.addEventListener('click', function () { manual(index + 1); });

    /* --- Swipe ---
       Pointer events cover mouse drag and touch alike. Nothing is
       preventDefault-ed, so a vertical swipe still scrolls the page. */
    var startX = null;
    var startY = null;

    wrap.addEventListener('pointerdown', function (e) {
      startX = e.clientX;
      startY = e.clientY;
    }, { passive: true });

    wrap.addEventListener('pointerup', function (e) {
      if (startX === null) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      startX = startY = null;

      // Only a mostly-horizontal drag counts, so it can't hijack page scroll.
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        manual(dx < 0 ? index + 1 : index - 1);
      }
    }, { passive: true });

    wrap.addEventListener('pointercancel', function () { startX = startY = null; });

    /* --- Autoplay --- */
    if (autoplayMs && !reduceMotion) {
      var play = function () {
        if (!stopped && !timer) {
          timer = setInterval(function () { show(index + 1); }, autoplayMs);
        }
      };

      // Don't cycle while the hero is off screen.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) {
            play();
          } else {
            clearInterval(timer);
            timer = null;
          }
        }, { threshold: 0.25 }).observe(root);
      } else {
        play();
      }
    }

    // Warm slide 2 only once the initial render is done.
    var warm = function () { loadSlide((index + 1) % slides.length); };
    if (document.readyState === 'complete') warm();
    else window.addEventListener('load', warm);
  }

  /* ------------------------------------------------------------------------
     FAQ accordion — native <details>, one open at a time, animated height
     ------------------------------------------------------------------------ */
  function setOpen(item, open) {
    if (item.open === open) return;

    if (item._anim) item._anim.cancel();

    var startHeight = item.getBoundingClientRect().height;
    item.open = open;                                  // forces the final layout
    var endHeight = item.getBoundingClientRect().height;

    if (reduceMotion) return;

    item._anim = item.animate(
      { height: [startHeight + 'px', endHeight + 'px'] },
      { duration: 250, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
    );
    item._anim.onfinish = function () { item._anim = null; };
  }

  function initAccordion(list) {
    var items = Array.prototype.slice.call(list.querySelectorAll('.faq__item'));

    items.forEach(function (item) {
      var summary = item.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', function (event) {
        event.preventDefault();
        var opening = !item.open;

        // Opening one closes the rest.
        if (opening) {
          items.forEach(function (other) {
            if (other !== item) setOpen(other, false);
          });
        }
        setOpen(item, opening);
      });
    });
  }

  /* ------------------------------------------------------------------------
     "Drive" button — split the label into per-letter spans so each can be
     animated on hover (effect after aaroniker's CodePen). The icon is left
     untouched; only text nodes become letters.
     ------------------------------------------------------------------------ */
  function splitButtonLetters(btn) {
    if (btn.querySelector('.btn__d')) return;              // already split

    Array.prototype.slice.call(btn.childNodes).forEach(function (node) {
      if (node.nodeType !== 3 || !node.textContent.trim()) return;  // text nodes only

      var wrap = document.createElement('span');
      wrap.className = 'btn__d';

      node.textContent.trim().split('').forEach(function (ch, i) {
        var s = document.createElement('span');
        s.textContent = ch;                                // spaces preserved via CSS
        s.style.setProperty('--d', (i * 0.035) + 's');     // stagger across the label
        wrap.appendChild(s);
      });

      btn.replaceChild(wrap, node);
    });
  }

  /* ------------------------------------------------------------------------
     Header CTA running-border — size the SVG rect to the button's exact
     border-box so the light rides dead-centre on the 2px border. Measured in
     px (not %) to avoid border-box/padding-box drift; re-measured on resize.
     ------------------------------------------------------------------------ */
  function initCtaBorder(btn) {
    var svg = btn.querySelector('.header__cta-border');
    if (!svg) return;

    // Build the comet as N stroke copies: the head layer runs furthest ahead at
    // full opacity, each following layer trails a little and is fainter, so the
    // overlap fades the tail out crisply (no blur).
    var LAYERS = 9;
    var SVGNS = 'http://www.w3.org/2000/svg';
    var rects = [];
    if (!svg.querySelector('rect')) {
      for (var i = 0; i < LAYERS; i++) {
        var rc = document.createElementNS(SVGNS, 'rect');
        rc.setAttribute('pathLength', '100');
        rc.style.opacity = Math.pow(1 - i / LAYERS, 1.5).toFixed(3);
        // Head (i=0) most advanced → most-negative delay; tail (last) at 0.
        rc.style.animationDelay = (-(LAYERS - 1 - i) * 0.03) + 's';
        svg.appendChild(rc);
        rects.push(rc);
      }
    } else {
      rects = Array.prototype.slice.call(svg.querySelectorAll('rect'));
    }

    function apply() {
      var r = btn.getBoundingClientRect();
      if (!r.width) return;

      var bw = parseFloat(getComputedStyle(btn).borderTopWidth) || 0;  // 2px
      // Inset by half the stroke so the 2px stroke lands centred on the border.
      var inset = bw / 2;
      var w = r.width - bw;
      var h = r.height - bw;

      rects.forEach(function (rect) {
        rect.setAttribute('x', inset);
        rect.setAttribute('y', inset);
        rect.setAttribute('width', w);
        rect.setAttribute('height', h);
        rect.setAttribute('rx', h / 2);   // full pill, matches the button
        rect.setAttribute('ry', h / 2);
      });
    }

    apply();
    if ('ResizeObserver' in window) {
      new ResizeObserver(apply).observe(btn);
    } else {
      window.addEventListener('resize', apply);
    }
    // Fonts can load after init and change the button width.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(apply);
  }

  /* ------------------------------------------------------------------------
     Modal — accessible dialog: focus trap, Esc, backdrop click, scroll lock.
     ------------------------------------------------------------------------ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';

  function initModals() {
    var openTrigger = null;

    function focusable(modal) {
      return Array.prototype.slice.call(modal.querySelectorAll(FOCUSABLE))
        .filter(function (el) { return el.offsetParent !== null; });
    }

    function open(modal, trigger) {
      openTrigger = trigger || null;
      modal.hidden = false;
      // Reflow so the dialog's transition runs from its hidden state. The
      // blurred backdrop itself has no transition, so it's there instantly.
      void modal.offsetWidth;
      modal.classList.add('is-open');

      var first = modal.querySelector('.modal__close') || focusable(modal)[0];
      if (first) first.focus();
    }

    function close(modal) {
      // Stop any video playing inside the popup so its sound doesn't linger.
      modal.querySelectorAll('video').forEach(function (v) {
        try { v.pause(); } catch (e) {}
      });

      modal.classList.remove('is-open');

      var done = function () {
        modal.hidden = true;
        modal.removeEventListener('transitionend', done);
      };
      if (reduceMotion) done();
      else modal.addEventListener('transitionend', done);

      if (openTrigger) { openTrigger.focus(); openTrigger = null; }
    }

    // Openers
    document.querySelectorAll('[data-modal-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        var modal = document.getElementById(trigger.getAttribute('data-modal-open'));
        if (!modal) return;
        e.preventDefault();
        open(modal, trigger);
      });
    });

    // Per-modal wiring
    document.querySelectorAll('.modal').forEach(function (modal) {
      // Close targets (backdrop, X)
      modal.querySelectorAll('[data-modal-close]').forEach(function (el) {
        el.addEventListener('click', function () { close(modal); });
      });

      // Keyboard: Esc closes, Tab is trapped inside.
      modal.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { close(modal); return; }
        if (e.key !== 'Tab') return;

        var items = focusable(modal);
        if (!items.length) return;
        var firstEl = items[0];
        var lastEl = items[items.length - 1];

        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      });

      // Occasion tabs: single active, and each swaps the main media panel.
      var tabs = Array.prototype.slice.call(modal.querySelectorAll('.modal__tab'));
      var panels = Array.prototype.slice.call(modal.querySelectorAll('[data-tab-panel]'));
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var id = tab.getAttribute('data-tab');
          // Stop any video that was playing in the panel we're leaving.
          modal.querySelectorAll('[data-tab-panel] video').forEach(function (v) {
            try { v.pause(); } catch (e) {}
          });
          tabs.forEach(function (t) {
            var on = t === tab;
            t.classList.toggle('is-active', on);
            t.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          panels.forEach(function (p) {
            p.classList.toggle('is-active', p.getAttribute('data-tab-panel') === id);
          });
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     Cookie consent — corner banner + settings popup.
     The settings popup opens through the normal modal wiring (its trigger has
     data-modal-open); here we only handle storing the choice and dismissing.
     ------------------------------------------------------------------------ */
  function initCookies() {
  var KEY = 'kinora-cookie-consent';
  var banner = document.getElementById('cookie-banner');
  var modal = document.getElementById('cookie-modal');

  // ===== СБРОС БЛОКИРУЮЩИХ КЛАССОВ =====
  document.documentElement.classList.remove('scroll-lock', 'overflow-hidden', 'no-scroll', 'cookie-open', 'modal-open');
  document.body.classList.remove('scroll-lock', 'overflow-hidden', 'no-scroll', 'cookie-open', 'modal-open');
  if (banner) {
    banner.style.pointerEvents = 'auto';
    banner.style.touchAction = 'auto';
    banner.style.display = '';
  }
  // =====================================

  if (!banner) return;

  var boxes = modal ? Array.prototype.slice.call(modal.querySelectorAll('[data-cookie-cat]')) : [];

  function stored() {
    try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  }

  function registerWithShopify(consent) {
    function apply() {
      try {
        window.Shopify.customerPrivacy.setTrackingConsent({
          analytics: !!consent.analytics,
          marketing: !!consent.marketing,
          preferences: !!consent.functional,
          sale_of_data: !!consent.marketing
        }, function () {});
      } catch (e) {}
    }
    if (window.Shopify && window.Shopify.customerPrivacy &&
        window.Shopify.customerPrivacy.setTrackingConsent) {
      apply();
    } else if (window.Shopify && window.Shopify.loadFeatures) {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (err) { if (!err) apply(); }
      );
    }
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    modal.classList.remove('is-open');
    modal.hidden = true;
    // Сброс при закрытии
    document.documentElement.classList.remove('scroll-lock', 'overflow-hidden', 'no-scroll');
    document.body.classList.remove('scroll-lock', 'overflow-hidden', 'no-scroll');
  }

  function save(consent) {
    consent.necessary = true;
    try { localStorage.setItem(KEY, JSON.stringify(consent)); } catch (e) {}
    banner.hidden = true;
    closeModal();
    syncBoxes(consent);
    registerWithShopify(consent);
    try {
      window.dispatchEvent(new CustomEvent('kinora:consent', { detail: consent }));
    } catch (e) {}
    // Дополнительный сброс
    document.documentElement.classList.remove('scroll-lock', 'overflow-hidden', 'no-scroll');
    document.body.classList.remove('scroll-lock', 'overflow-hidden', 'no-scroll');
  }

  function syncBoxes(consent) {
    boxes.forEach(function (box) {
      if (box.disabled) { box.checked = true; return; }
      box.checked = !!(consent && consent[box.getAttribute('data-cookie-cat')]);
    });
  }

  function every(value) {
    var consent = {};
    boxes.forEach(function (box) {
      var id = box.getAttribute('data-cookie-cat');
      consent[id] = box.disabled ? true : value;
    });
    return consent;
  }

  document.querySelectorAll('[data-cookie-accept]').forEach(function (b) {
    b.addEventListener('click', function () { save(every(true)); });
  });

  document.querySelectorAll('[data-cookie-necessary]').forEach(function (b) {
    b.addEventListener('click', function () { save(every(false)); });
  });

  document.querySelectorAll('[data-cookie-save]').forEach(function (b) {
    b.addEventListener('click', function () {
      var consent = {};
      boxes.forEach(function (box) {
        consent[box.getAttribute('data-cookie-cat')] = box.disabled ? true : box.checked;
      });
      save(consent);
    });
  });

  var decision = stored();

  if (decision) {
    syncBoxes(decision);
    registerWithShopify(decision);
  } else {
    syncBoxes({});
    banner.hidden = false;
  }

  if (!modal) return;

  function bounceBack() {
    if (!stored()) banner.hidden = false;
    // Сброс при закрытии модалки через Escape/backdrop
    document.documentElement.classList.remove('scroll-lock', 'overflow-hidden', 'no-scroll');
    document.body.classList.remove('scroll-lock', 'overflow-hidden', 'no-scroll');
  }
  modal.querySelectorAll('[data-modal-close]').forEach(function (el) {
    el.addEventListener('click', bounceBack);
  });
  modal.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') bounceBack();
  });

  document.querySelectorAll('[data-cookie-settings]').forEach(function (link) {
    link.addEventListener('click', function () {
      syncBoxes(stored() || {});
      banner.hidden = true;
    });
  });
}

  /* ------------------------------------------------------------------------
     Video facade — a poster with a play button that swaps in the real video
     only on click (keeps the page light; nothing streams until asked for).
     Markup contract:
       [data-video-facade]           wrapper
         [data-video-open]           the clickable poster/button
         <template data-video-markup> the <video>/<iframe> to inject
     ------------------------------------------------------------------------ */
  function initVideoFacades() {
    document.querySelectorAll('[data-video-facade]').forEach(function (wrap) {
      var poster = wrap.querySelector('[data-video-poster]');
      var tpl = wrap.querySelector('template[data-video-markup]');
      if (!poster || !tpl) return;

      poster.addEventListener('click', function (e) {
        e.preventDefault();
        var frag = tpl.content ? tpl.content.cloneNode(true) : null;
        if (!frag) return;
        wrap.classList.add('is-playing');
        var media = frag.querySelector('video');
        // Swap the poster for the real player, in the same spot.
        poster.replaceWith(frag);
        tpl.remove();
        // Clicking is a user gesture → play with sound.
        if (media) {
          media.muted = false;
          media.volume = 1;
          try {
            var playing = media.play();
            if (playing && playing.catch) {
              playing.catch(function () { media.muted = true; media.play().catch(function () {}); });
            }
          } catch (err) {}
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     Lightbox — opens a story photo or video fullscreen. The media lives in a
     <template> next to its poster, so nothing loads until it is opened.
       [data-lightbox]              wrapper
         [data-lightbox-open]       the clickable poster
         <template data-lightbox-content>  the <img>/<video> to show
     ------------------------------------------------------------------------ */
  function initLightbox() {
    var box = document.getElementById('kinora-lightbox');
    if (!box) return;

    var stage = box.querySelector('[data-lightbox-stage]');
    var opener = null;

    function open(tpl, trigger) {
      var frag = tpl.content ? tpl.content.cloneNode(true) : null;
      if (!frag) return;

      stage.innerHTML = '';
      stage.appendChild(frag);
      opener = trigger || null;

      box.hidden = false;
      void box.offsetWidth;                 // reflow so the transition runs
      box.classList.add('is-open');

      // Opened by a click (a user gesture), so sound is allowed: unmute and play.
      var media = stage.querySelector('video');
      if (media) {
        media.muted = false;
        media.volume = 1;
        media.removeAttribute('muted');
        try {
          var playing = media.play();
          if (playing && playing.catch) {
            playing.catch(function () {
              // If the browser still refuses sound, fall back to muted playback.
              media.muted = true;
              media.play().catch(function () {});
            });
          }
        } catch (e) {}
      }

      var close = box.querySelector('.lightbox__close');
      if (close) close.focus();
    }

    function close() {
      var media = stage.querySelector('video');
      if (media) { try { media.pause(); } catch (e) {} }

      box.classList.remove('is-open');

      var done = function () {
        box.hidden = true;
        stage.innerHTML = '';            // release the media
        box.removeEventListener('transitionend', done);
      };
      if (reduceMotion) done();
      else box.addEventListener('transitionend', done);

      if (opener) { opener.focus(); opener = null; }
    }

    document.querySelectorAll('[data-lightbox]').forEach(function (wrap) {
      var trigger = wrap.querySelector('[data-lightbox-open]');
      var tpl = wrap.querySelector('template[data-lightbox-content]');
      if (!trigger || !tpl) return;
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        open(tpl, trigger);
      });
    });

    box.querySelectorAll('[data-lightbox-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !box.hidden) close();
    });
  }

  /* ------------------------------------------------------------------------
     Stories marquee — drifts the row by scrollLeft rather than a transform, so
     it stays a real scroll container: on touch the visitor can grab it and
     scroll on like a slider, and it picks the drift back up afterwards.
     Hovering deliberately does NOT pause it.
     ------------------------------------------------------------------------ */
  function initMarquee(root) {
    var track = root.querySelector('.stories__marquee-track');
    var firstSet = root.querySelector('.stories__marquee-set');
    if (!track || !firstSet) return;

    var duration = parseFloat(root.getAttribute('data-marquee-duration')) || 45;
    var paused = reduceMotion;          // no self-motion when reduced motion is on
    var visible = true;
    var resumeTimer = null;
    var last = 0;
    var pausedAt = 0;
    // Our own sub-pixel position. WebKit rounds scrollLeft to whole pixels, so
    // `scrollLeft += 0.7` each frame would round away to nothing and never move
    // — we keep the float here and assign it instead.
    var pos = 0;

    // Cache the loop period (one set's width). Reading offsetWidth every frame
    // forced a synchronous layout ("forced reflow"); measure once + on resize.
    var periodW = firstSet.offsetWidth;
    var rafId = null;
    window.addEventListener('resize', function () { periodW = firstSet.offsetWidth; });

    function frame(now) {
      if (!last) last = now;
      var dt = (now - last) / 1000;
      last = now;

      // Safety net: never stay stuck on pause unless the visitor took over —
      // some browsers swallow the touch end that would have resumed us.
      if (paused && !handedOver && !reduceMotion && pausedAt && now - pausedAt > 2500) {
        pos = root.scrollLeft;
        paused = false;
        pausedAt = 0;
      }

      if (!paused) {
        var w = periodW;
        if (w) {
          pos += (w / duration) * dt;
          if (pos >= w) pos -= w;
          root.scrollLeft = pos;         // write only — no layout read per frame
        }
      }
      rafId = requestAnimationFrame(frame);
    }

    // A touch only hands the strip over once the visitor actually swipes it
    // sideways. Merely resting a finger on it while scrolling the page down
    // must not kill the drift — that stopped it the moment you reached the
    // section on a phone.
    var handedOver = false;
    var startX = 0;
    var startY = 0;
    var decided = false;

    function stop() {                    // sideways swipe: the strip is theirs now
      handedOver = true;
      paused = true;
      clearTimeout(resumeTimer);
      root.classList.add('is-stopped');
    }

    function hold() {                    // finger/button down: pause, decide later
      if (handedOver) return;
      paused = true;
      pausedAt = performance.now();
      clearTimeout(resumeTimer);
    }

    function resumeSoon(delay) {
      if (handedOver || reduceMotion) return;
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        pos = root.scrollLeft;           // pick up wherever the visitor left it
        last = 0;                        // don't jump after the idle gap
        pausedAt = 0;
        paused = false;
      }, delay || 1200);
    }

    root.addEventListener('touchstart', function (e) {
      var t = e.touches && e.touches[0];
      if (!t) return;
      startX = t.clientX;
      startY = t.clientY;
      decided = false;
      hold();
    }, { passive: true });

    root.addEventListener('touchmove', function (e) {
      if (decided || handedOver) return;
      var t = e.touches && e.touches[0];
      if (!t) return;
      var dx = Math.abs(t.clientX - startX);
      var dy = Math.abs(t.clientY - startY);
      if (dx < 8 && dy < 8) return;      // too small to tell the direction yet
      decided = true;
      if (dx > dy) stop();               // horizontal → hand the strip over
      else resumeSoon(600);              // vertical page scroll → carry on
    }, { passive: true });

    root.addEventListener('touchend', function () {
      if (!decided) resumeSoon(600);     // just a tap (e.g. opening the lightbox)
    }, { passive: true });

    // iOS fires this instead of touchend once the page scroll takes the gesture
    // over — without it the strip would stay paused for good.
    root.addEventListener('touchcancel', function () {
      if (!decided) resumeSoon(600);
    }, { passive: true });

    // Mouse / trackpad: a brief pause, then it drifts on.
    root.addEventListener('mousedown', function () { hold(); resumeSoon(2000); });
    root.addEventListener('wheel', function () { hold(); resumeSoon(2000); }, { passive: true });

    // Only run the rAF loop while the section is on screen — no animation work
    // during the initial load (it's below the fold) or when scrolled past.
    function startLoop() { if (rafId === null) { last = 0; rafId = requestAnimationFrame(frame); } }
    function stopLoop() { if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; } }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) { startLoop(); } else { stopLoop(); }
      }, { threshold: 0 }).observe(root);
    } else {
      startLoop();
    }
  }

  /* ------------------------------------------------------------------------
     Any link that leaves the current page opens in a new tab. In-page anchors,
     mailto/tel and modal triggers are left alone, and anything that already
     declares a target keeps it.
     ------------------------------------------------------------------------ */
  function initExternalLinks() {
    var scope = document.querySelectorAll('main a[href], .footer a[href]');

    Array.prototype.forEach.call(scope, function (a) {
      if (a.target) return;                                 // already decided
      if (a.hasAttribute('data-modal-open')) return;        // opens a popup
      if (a.hasAttribute('data-lightbox-open')) return;

      var href = a.getAttribute('href') || '';
      if (!href || href.charAt(0) === '#') return;          // same-page anchor
      if (/^(mailto:|tel:|sms:|javascript:)/i.test(href)) return;

      a.target = '_blank';
      a.rel = a.rel ? a.rel + ' noopener' : 'noopener';
    });
  }

  /* ------------------------------------------------------------------------
     Muted preview videos only play (and download) once scrolled into view, so
     they never compete with the hero image on the initial mobile load.
     ------------------------------------------------------------------------ */
    function initInViewVideos() {
    if (reduceMotion) return;
    var vids = document.querySelectorAll('video.js-inview-video');
    if (!vids.length) return;

    function start(v) {
      if (v.dataset.userUnmuted !== 'true') v.muted = true;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }

    // Оптимизация: если видео уже в видимой области, запускаем сразу
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var v = e.target;
          v.dataset.inview = e.intersectionRatio > 0 ? '1' : '';
          if (e.intersectionRatio >= 0.25) {
            start(v);
          } else if (e.intersectionRatio === 0) {
            try { v.pause(); } catch (err) {}
          }
        });
      }, { threshold: [0, 0.25] });

      vids.forEach(function (v) { 
        io.observe(v);
        // Если видео уже видно (например, первый слайд), запускаем без ожидания скролла
        if (v.getBoundingClientRect().top < window.innerHeight) {
           start(v);
        }
      });
    }

    function retry() {
      document.removeEventListener('touchstart', retry);
      document.removeEventListener('click', retry);
      document.removeEventListener('scroll', retry);
      Array.prototype.forEach.call(vids, function (v) {
        if (v.paused && v.dataset.inview === '1') start(v);
      });
    }
    document.addEventListener('touchstart', retry, { passive: true, once: true });
    document.addEventListener('click', retry, { once: true });
    document.addEventListener('scroll', retry, { passive: true, once: true });
  }

  /* ------------------------------------------------------------------------
     Unmute overlay — the video autoplays muted (browsers allow nothing else),
     so the sound needs one real click to turn on. After that the overlay is
     gone for good and the native controls take over.
       [data-unmute-video]   wrapper holding the <video>
         [data-unmute-btn]   the icon + label sitting on top of it
     ------------------------------------------------------------------------ */
  function initUnmuteOverlays() {
    document.querySelectorAll('[data-unmute-video]').forEach(function (wrap) {
      var btn = wrap.querySelector('[data-unmute-btn]');
      var video = wrap.querySelector('video');
      if (!btn || !video) return;

      // Safari honours the `muted` property far more reliably than the markup
      // attribute, and it needs the play() call retried once there is data.
      // This runs early and on its own — initInViewVideos is deferred and is
      // skipped entirely when the visitor asked for reduced motion.
      function kick() {
        if (video.dataset.userUnmuted === 'true' || !video.paused) return;
        // Properties, not just the markup attributes — some Safari builds only
        // consult these when deciding whether playback may start unprompted.
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }

      kick();
      video.addEventListener('loadedmetadata', kick);
      video.addEventListener('canplay', kick);

      // iOS releases blocked playback after an interaction, but the first one
      // usually happens far above this section — so keep listening until the
      // video is genuinely running rather than spending the chance on scroll
      // number one, and try again each time it scrolls back into view.
      var RETRY_ON = ['touchstart', 'pointerdown', 'click', 'scroll', 'keydown'];
      function retry() {
        kick();
        if (!video.paused) {
          RETRY_ON.forEach(function (name) { document.removeEventListener(name, retry); });
        }
      }
      RETRY_ON.forEach(function (name) {
        document.addEventListener(name, retry, { passive: true });
      });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) kick();
        }, { threshold: 0.25 }).observe(video);
      }

      function activate(e) {
        if (wrap.classList.contains('is-unmuted')) return;
        if (e) e.preventDefault();
        // Flag it so the in-view observer below stops re-muting on every scroll.
        video.dataset.userUnmuted = 'true';
        video.muted = false;
        video.volume = 1;
        video.controls = true;         // held back until now, see the section
        wrap.classList.add('is-unmuted');
        try {
          var playing = video.play();
          if (playing && playing.catch) playing.catch(function () {});
        } catch (err) {}
      }

      // Touch is handled on pointerup rather than click: iOS spends the first
      // tap on the :hover state and swallows the click that should follow.
      // A pointer that travelled is a scroll, not a tap — ignore it, or the
      // sound would come on every time someone swipes across the video.
      var TAP_SLOP = 10;
      var x = 0, y = 0, moved = false;

      btn.addEventListener('pointerdown', function (e) {
        x = e.clientX; y = e.clientY; moved = false;
      });
      btn.addEventListener('pointermove', function (e) {
        if (Math.abs(e.clientX - x) > TAP_SLOP || Math.abs(e.clientY - y) > TAP_SLOP) moved = true;
      });
      btn.addEventListener('pointercancel', function () { moved = true; });
      btn.addEventListener('pointerup', function (e) {
        if (!moved) activate(e);
      });

      // Mouse and keyboard still arrive here; activate() is idempotent.
      btn.addEventListener('click', activate);
    });
  }

  /* ------------------------------------------------------------------------
     Back to top — appears once the visitor has scrolled past the first screen.
     ------------------------------------------------------------------------ */
  function initBackToTop() {
    var btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;

    var SHOW_AFTER = 400;                  // px scrolled before it appears
    var queued = false;

    function update() {
      queued = false;
      btn.classList.toggle('is-visible', window.pageYOffset > SHOW_AFTER);
    }

    window.addEventListener('scroll', function () {
      if (!queued) {
        queued = true;
        requestAnimationFrame(update);
      }
    }, { passive: true });

    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });

    update();
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  function init() {
    // Lightweight and/or needed early — cheap event wiring only.
    document.querySelectorAll('.faq__list').forEach(initAccordion);
    initVideoFacades();
    initUnmuteOverlays();
    initLightbox();
    initExternalLinks();
    initBackToTop();
    initModals();
    initCookies();

    // Heavier, non-critical work (measuring, cloning, animation) is pushed past
    // the first paint so it never competes with the LCP render on slow devices.
    var deferred = function () {
      document.querySelectorAll('.btn--primary').forEach(splitButtonLetters);
      document.querySelectorAll('[data-hero-slider]').forEach(initHeroSlider);
      document.querySelectorAll('[data-carousel]').forEach(initCarousel);
      document.querySelectorAll('[data-marquee]').forEach(initMarquee);
      document.querySelectorAll('.header__cta').forEach(initCtaBorder);
      initInViewVideos();
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(deferred, { timeout: 2500 });
    } else {
      setTimeout(deferred, 200);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
